import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, ShieldCheck, ArrowLeft, Zap } from "lucide-react";
import { PACKAGES, SLOT_LABELS, baseFor, computeTotal, currentSlot } from "@/lib/mockStore";
import { paymentApi, readError } from "@/lib/api";import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const money = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);

const Payment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preferredPkg = searchParams.get("package");
  const { user, updateProfile } = useAuth();
  const [step, setStep] = useState(
    user?.paymentStatus === "pending" || user?.paymentStatus === "verified"
      ? "submitted"
      : "choose",
  ); // choose | submitted
  const [pickedId, setPickedId] = useState(
    user?.packageId || preferredPkg || "delegate-api",
  );
  const slot = user?.paymentStatus === "verified" && user?.slot ? user.slot : currentSlot();
  const slotLabel = SLOT_LABELS[slot] || "Early-bird";
  const [rzpLoading, setRzpLoading] = useState(false);
  const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || "";

  const picked = PACKAGES.find((p) => p.id === pickedId) || PACKAGES[0];
  const base = baseFor(picked, slot);
  const total = computeTotal(base);

  // Lazy-load the Razorpay Standard Checkout script only when the user taps
  // the Pay CTA. Keeps the bundle light on other routes.
  const loadRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (typeof window === "undefined") {
        reject(new Error("No window"));
        return;
      }
      if (window.Razorpay) {
        resolve(window.Razorpay);
        return;
      }
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(window.Razorpay);
      s.onerror = () => reject(new Error("Razorpay script failed to load."));
      document.body.appendChild(s);
    });

  const startRazorpay = async () => {
    if (!RAZORPAY_KEY_ID) {
      toast.error("Online payment is temporarily unavailable. Please try again in a moment.");
      return;
    }
    setRzpLoading(true);
    try {
      await loadRazorpayScript();
      const order = await paymentApi.createRazorpayOrder({
        packageId: picked.id,
      });
      const rzp = new window.Razorpay({
        key: order.key_id || RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "APICON 2026",
        description: `${picked.label} · ${slotLabel}`,
        image:
          "https://customer-assets-rejwkqb3.emergentagent.net/job_apicon-register/artifacts/1q137q9b_f04cf28b-14e3-4e14-b6c6-e1574fbf1d60.JPG",
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.phone || "",
        },
        theme: { color: "#0B3D7A" },
        modal: {
          ondismiss: () => {
            setRzpLoading(false);
            toast("Payment cancelled. You can try again anytime.");
          },
        },
        handler: async (resp) => {
          try {
            await paymentApi.verifyRazorpay({
              razorpayOrderId: resp.razorpay_order_id,
              razorpayPaymentId: resp.razorpay_payment_id,
              razorpaySignature: resp.razorpay_signature,
              packageId: picked.id,
            });
            await updateProfile();
            setStep("submitted");
            toast.success("Payment successful — you're registered for APICON 2026!");
          } catch (err) {
            toast.error(readError(err) || "Payment could not be verified. Contact support if amount was debited.");
          } finally {
            setRzpLoading(false);
          }
        },
      });
      rzp.on("payment.failed", (resp) => {
        setRzpLoading(false);
        toast.error(resp?.error?.description || "Payment failed. Please try again.");
      });
      rzp.open();
    } catch (err) {
      setRzpLoading(false);
      toast.error(readError(err) || "Could not start the payment.");
    }
  };

  return (
    <div className="relative bg-white" data-testid="page-payment">
      <section className="relative overflow-hidden bg-apicon-mist/50 py-8 md:py-20">
        <div className="pointer-events-none absolute inset-0 heritage-pattern opacity-60" />
        <div className="relative mx-auto w-full max-w-7xl px-4 md:px-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="mb-4 inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.16em] text-apicon-navy hover:text-apicon-green md:mb-6 md:text-xs"
            data-testid="payment-back-to-dashboard"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
          </button>
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-green md:text-xs">
            Registration payment
          </div>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-apicon-navy sm:text-5xl md:mt-3">
            Choose your package. <br />
            <span className="italic text-apicon-green">Pay in a tap.</span>
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-gray-600 md:mt-4 md:text-base">
            All packages include the 3-day conference kit, workshop access and
            the cultural evenings.
          </p>
        </div>
      </section>

      <section className="relative py-8 md:py-20">
        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-6 px-4 md:px-8 lg:grid-cols-12 lg:gap-10">
          {/* Left: packages / QR / status */}
          <div className="lg:col-span-8">
            {step === "choose" && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                data-testid="payment-step-choose"
              >
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
                      Current pricing
                    </div>
                    <div className="mt-1 font-display text-2xl font-semibold text-apicon-navy">
                      Registering as
                    </div>
                  </div>
                  <div
                    className="rounded-full border border-gray-200 bg-white px-5 py-2 text-sm font-medium text-apicon-navy"
                    data-testid="payment-current-slot"
                  >
                    {slot === "regular"
                      ? "Early-bird · till 15 Nov 2026, 11:59 PM"
                      : `${slotLabel} registration`}
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-3 sm:mt-8 sm:grid-cols-2 sm:gap-5">
                  {PACKAGES.map((pkg) => {
                    const b = baseFor(pkg, slot);
                    const t = computeTotal(b);
                    const active = pkg.id === pickedId;
                    return (
                      <button
                        key={pkg.id}
                        type="button"
                        onClick={() => setPickedId(pkg.id)}
                        className={cn(
                          "group relative overflow-hidden rounded-2xl border p-4 text-left transition-all duration-300 sm:rounded-3xl sm:p-6",
                          active
                            ? "border-apicon-navy bg-white shadow-xl shadow-apicon-navy/10"
                            : "border-gray-200 bg-white hover:border-apicon-navy/40 hover:shadow-md",
                        )}
                        data-testid={`payment-package-${pkg.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="font-display text-base font-semibold text-apicon-navy sm:text-lg">
                            {pkg.label}
                          </div>
                          <span
                            className={cn(
                              "flex h-6 w-6 items-center justify-center rounded-full border transition-colors",
                              active
                                ? "border-apicon-navy bg-apicon-navy text-white"
                                : "border-gray-300",
                            )}
                          >
                            {active && <CheckCircle2 className="h-4 w-4" />}
                          </span>
                        </div>
                        <p className="mt-1.5 text-xs text-gray-600 sm:mt-2 sm:text-sm">
                          {pkg.description}
                        </p>
                        <div className="mt-4 flex items-end justify-between sm:mt-5">
                          <div>
                            <div className="font-display text-2xl font-bold text-apicon-navy sm:text-3xl">
                              {money(t)}
                            </div>
                            <div className="text-[11px] text-gray-500 sm:text-xs">
                              {money(b)} + 18% GST
                            </div>
                          </div>
                          <Badge
                            variant="outline"
                            className={cn(
                              "rounded-full border-none text-[10px] uppercase tracking-[0.18em]",
                              slot === "regular"
                                ? "bg-apicon-green/10 text-apicon-green"
                                : "bg-apicon-gold/20 text-apicon-navy",
                            )}
                          >
                            {slotLabel}
                          </Badge>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-6 flex flex-col gap-4 rounded-2xl bg-apicon-navy p-5 text-white sm:mt-10 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:rounded-3xl sm:p-6">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-gold sm:text-xs">
                      Total to pay
                    </div>
                    <div className="mt-1 font-display text-2xl font-bold sm:text-3xl">
                      {money(total)}
                    </div>
                    <div className="mt-1 text-[11px] text-white/70 sm:text-xs">
                      {picked.label} · {slotLabel}
                    </div>
                  </div>
                  <Button
                    onClick={startRazorpay}
                    disabled={rzpLoading}
                    size="lg"
                    className="w-full rounded-full bg-apicon-gold px-7 font-semibold text-apicon-navy hover:bg-[#ffb400] disabled:opacity-70 sm:w-auto"
                    data-testid="payment-proceed-cta"
                  >
                    <Zap className="mr-2 h-4 w-4" />
                    {rzpLoading ? "Opening payment…" : `Pay ${money(total)} securely`}
                  </Button>
                </div>
                <p className="mt-3 text-center text-[11px] text-gray-500 sm:text-left">
                  Secured by <span className="font-semibold text-apicon-navy">Razorpay</span> — UPI, cards, wallets & netbanking. Your dashboard is unlocked the moment payment clears.
                </p>
              </motion.div>
            )}



            {step === "submitted" && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:rounded-3xl sm:p-8 md:p-10"
                data-testid="payment-step-submitted"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-apicon-green/10 text-apicon-green sm:h-14 sm:w-14">
                  <CheckCircle2 className="h-6 w-6 sm:h-7 sm:w-7" />
                </span>
                <h2 className="mt-5 font-display text-2xl font-bold text-apicon-navy sm:mt-6 sm:text-3xl">
                  Payment successful.
                </h2>
                <p className="mt-2.5 max-w-lg text-sm text-gray-600 sm:mt-3 sm:text-base">
                  You&rsquo;re confirmed for APICON 2026. A receipt is on its
                  way to your email, and your delegate dashboard is unlocked.
                </p>

                <div className="mt-6 grid grid-cols-1 gap-3 sm:mt-8 sm:grid-cols-2 sm:gap-4">
                  <div className="rounded-2xl bg-apicon-mist/50 p-4 sm:p-5">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-navy/70">
                      Package
                    </div>
                    <div className="mt-1 font-display text-lg font-semibold text-apicon-navy">
                      {picked.label}
                    </div>
                    <div className="text-xs text-gray-500">
                      {slotLabel} · {money(total)}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-apicon-mist/50 p-4 sm:p-5">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-navy/70">
                      Invoice
                    </div>
                    <div className="mt-1 truncate font-mono text-sm text-apicon-navy" data-testid="payment-invoice-no">
                      {user?.invoiceNo || user?.txnRef || "—"}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3 sm:mt-8">
                  <Button
                    onClick={() => navigate("/dashboard")}
                    className="rounded-full bg-apicon-navy font-semibold text-white hover:bg-[#082C5A]"
                    data-testid="payment-goto-dashboard"
                  >
                    Go to dashboard
                  </Button>
                  {user?.invoiceNo && (
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          const blob = await paymentApi.downloadInvoice();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `${user.invoiceNo.replace(/\//g, "-")}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          URL.revokeObjectURL(url);
                        } catch (err) {
                          toast.error(readError(err) || "Could not download the invoice.");
                        }
                      }}
                      className="rounded-full border-apicon-navy/20 text-apicon-navy hover:bg-apicon-mist"
                      data-testid="payment-download-invoice"
                    >
                      Download GST invoice
                    </Button>
                  )}
                </div>
              </motion.div>
            )}
          </div>

          {/* Right: summary rails */}
          <aside className="space-y-4 lg:col-span-4">
            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:rounded-3xl sm:p-6">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-green sm:text-xs">
                Order summary
              </div>
              <div className="mt-3 space-y-2.5 text-sm sm:mt-4 sm:space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Package</span>
                  <span className="font-medium text-apicon-navy">
                    {picked.label}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Slot</span>
                  <span className="font-medium text-apicon-navy">
                    {slotLabel}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Base fee</span>
                  <span className="font-medium text-apicon-navy">
                    {money(base)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">GST (18%)</span>
                  <span className="font-medium text-apicon-navy">
                    {money(total - base)}
                  </span>
                </div>
                <div className="my-2 h-px bg-gray-100" />
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-sm text-apicon-navy">
                    Total
                  </span>
                  <span className="font-display text-2xl font-bold text-apicon-navy">
                    {money(total)}
                  </span>
                </div>
              </div>
            </div>

            <div className="hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:block sm:rounded-3xl sm:p-6">
              <ShieldCheck className="h-6 w-6 text-apicon-green" />
              <div className="mt-3 font-display text-lg font-semibold text-apicon-navy">
                Safe &amp; simple
              </div>
              <ul className="mt-3 space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 text-apicon-gold" />
                  Pay by UPI, credit / debit card, netbanking or wallet — all through Razorpay.
                </li>
                <li className="flex items-start gap-2">
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 text-apicon-gold" />
                  Your seat is locked in the second the payment clears — no manual verification.
                </li>
                <li className="flex items-start gap-2">
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 text-apicon-gold" />
                  Refunds honoured until 30 Sept 2026 (minus GST).
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Payment;
